import { useState, useEffect } from 'react';
import { Database } from '../types/supabase';
import { Trash2, Edit2, Calendar, FileText } from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { KanbanCard } from './KanbanCard';
import { KanbanColumn } from './KanbanColumn';
import TaskDetails from './TaskDetails';
import { supabase } from '../lib/supabase';

type Task = Database['public']['Tables']['tasks']['Row'];
type Employee = Database['public']['Tables']['employees']['Row'];

interface KanbanViewProps {
  tasks: Task[];
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onToggleComplete: (taskId: string, completed: boolean) => void;
  onUpdatePriority: (taskId: string, priority: string) => void;
  onUpdateStatus?: (taskId: string, status: string) => void;
}

export default function KanbanView({
  tasks,
  onEdit,
  onDelete,
  onToggleComplete,
  onUpdatePriority,
  onUpdateStatus
}: KanbanViewProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskEmployees, setTaskEmployees] = useState<Record<string, Employee[]>>({});
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Prevent drag from starting on buttons
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    })
  );
  const columns = ['Low', 'Normal', 'High', 'Urgent'] as const;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'Normal':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
      case 'High':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200';
      case 'Urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Not Started':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
      case 'Testing':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200';
      case 'Awaiting Feedback':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200';
      case 'Completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await onDelete(taskId);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const { data, error } = await supabase
          .from('task_assignments_with_team_members')
          .select('*')
          .in('task_id', tasks.map(t => t.id));

        if (error) throw error;

        const membersByTask: Record<string, Employee[]> = {};
        data.forEach((assignment: any) => {
          if (!membersByTask[assignment.task_id]) {
            membersByTask[assignment.task_id] = [];
          }
          if (assignment) {
            membersByTask[assignment.task_id].push({
              id: assignment.team_member_id,
              name: assignment.team_member_name,
              email: assignment.team_member_email,
              job_title: assignment.team_member_job_title,
              photo_url: assignment.team_member_photo_url
            });
          }
        });

        setTaskMembers(membersByTask);
      } catch (error) {
        console.error('Error fetching assignments:', error);
        // Don't show error toast to users, just log it
        console.warn('Team members may be unavailable');
      }
    };

    if (tasks.length > 0) {
      fetchAssignments();
    }
  }, [tasks]);

  const handleViewTask = (task: Task) => {
    setSelectedTask(task);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || !active) return;
    
    const taskId = active.id as string;
    const task = tasks.find((t) => t.id === taskId);
    const newPriority = over.id as string;
    
    // Validate that the new priority is one of the allowed values
    if (task && task.priority !== newPriority && 
        ['Low', 'Normal', 'High', 'Urgent'].includes(newPriority)) {
      onUpdatePriority(taskId, newPriority);
    }
    
    setActiveId(null);
  };

  const activeTask = activeId ? tasks.find((task) => task.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      modifiers={[]}
      onDragStart={({ active }) => setActiveId(active.id as string)}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-6 overflow-x-auto pb-6 px-1">
        {columns.map((priority) => {
          const columnTasks = tasks.filter((task) => task.priority === priority);
          const columnClass = priority === 'Urgent'
            ? 'bg-red-50/80 dark:bg-red-900/20' 
            : priority === 'High'
            ? 'bg-orange-50/80 dark:bg-orange-900/20'
            : priority === 'Normal'
            ? 'bg-blue-50/80 dark:bg-blue-900/20'
            : 'bg-gray-50/80 dark:bg-gray-900/20';

          return (
            <KanbanColumn
              key={priority}
              id={priority}
              title={priority}
              tasks={columnTasks}
              className={columnClass}
              onEdit={onEdit}
              onDelete={handleDelete}
              onViewTask={handleViewTask}
              onToggleComplete={onToggleComplete}
              onUpdateStatus={onUpdateStatus}
            />
          );
        })}
      </div>
      <DragOverlay>
        {activeId && activeTask ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 w-[300px]">
            <KanbanCard
              task={activeTask}
              onViewTask={handleViewTask}
              onEdit={onEdit}
              onDelete={handleDelete}
              onToggleComplete={onToggleComplete}
              onUpdateStatus={onUpdateStatus}
            />
          </div>
        ) : null}
      </DragOverlay>
      {selectedTask && (
        <TaskDetails
          task={selectedTask}
          employees={taskEmployees[selectedTask.id] || []}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </DndContext>
  );
}